
import { useQuery } from "@tanstack/react-query";
import { supabase } from '../lib/supabaseClient';
import { useToast } from "./use-toast";
import { useAccount } from "@/contexts/AccountContext";

interface FormattedEvent {
  animalId: string;
  animalName: string;
  event: string;
  date: string;
}

export const useRecentActivity = () => {
  const { toast } = useToast();
  const { currentAccount } = useAccount();

  return useQuery({
    queryKey: ["recentActivity", currentAccount?.account_id],
    queryFn: async () => {
      try {
        if (!currentAccount) {
          return [];
        }

        const { data: events, error: eventsError } = await supabase
          .from("animal_events")
          .select('id, event_type, date, animal_id')
          .eq('account_id', currentAccount.account_id)
          .order('date', { ascending: false })
          .limit(5);

        if (eventsError) {
          console.error('Error fetching events:', eventsError);
          toast({
            title: "Error fetching recent activity",
            description: eventsError.message,
            variant: "destructive",
          });
          return [];
        }

        if (!events?.length) {
          return [];
        }

        const animalIds = events.map(event => event.animal_id);
        
        const { data: animals, error: animalsError } = await supabase
          .from("animals")
          .select('id, name')
          .in('id', animalIds)
          .eq('account_id', currentAccount.account_id);

        if (animalsError) {
          console.error('Error fetching animal names:', animalsError);
          return events.map((event): FormattedEvent => ({
            animalId: event.animal_id,
            animalName: "Unknown",
            event: event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1),
            date: new Date(event.date).toLocaleDateString(),
          }));
        }

        // Create a map of animal names for quick lookup
        const animalNames = new Map(animals?.map(animal => [animal.id, animal.name]) || []);

        // Combine the data
        return events.map((event): FormattedEvent => ({
          animalId: event.animal_id,
          animalName: animalNames.get(event.animal_id) || "Unknown",
          event: event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1),
          date: new Date(event.date).toLocaleDateString(),
        }));

      } catch (error) {
        console.error('Unexpected error:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
        return [];
      }
    },
    retry: 1,
    enabled: !!currentAccount,
  });
};
